import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import Select from "@/components/ui/Select";
import Skeleton from "@/components/shared/Skeleton";
import { TenantService } from "@/services/tenantService";

// Top-of-page tenant picker. Feeds `tenantId` down into every tab.
//
// Data source note: uses `TenantService.fetchTenantList` (backed by
// /api/v2/admin/tenants), NOT `/platform/tenant-databases`. The latter
// only lists DEDICATED-tier tenants — /admin/tenants lists every tenant
// regardless of tier, which is what the audit dashboard needs so an SA
// can inspect shared-tier tenants too. No `isActive` filter either: a
// suspended tenant still has an audit trail worth reading.
//
// This used to be its own trigger, dropdown, search box and outside-click
// handler — the app's `Select` with different class names, and without the
// theme fixes `Select` had picked up, so the trigger and its rows read as
// near-black text under [data-theme="dark"]. The whole list is fetched once
// and `Select` searches it client-side, which also drops the debounced round
// trip per keystroke.
const TenantPicker = ({ value, onChange }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    TenantService.fetchTenantList({ pagination: false })
      .then((res) => {
        if (cancelled) return;
        // /admin/tenants shape: { success, data: { tenants: [ { id, slug, name } ] } }
        const rows = (res?.data?.tenants ?? []).map((t) => ({
          tenantId: t.id,
          tenantSlug: t.slug ?? "",
          // What the reader recognises. The slug is the identifier, not the
          // label, so it only stands in when a tenant has no name.
          tenantName: t.name || t.slug || "",
        }));
        rows.sort((a, b) => a.tenantName.localeCompare(b.tenantName));
        setTenants(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setTenants([]);
        const msg =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load tenants";
        toast.error(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(
    () => tenants.map((t) => ({ label: t.tenantName, value: t.tenantId })),
    [tenants],
  );

  if (loading && !options.length) {
    return <Skeleton className="h-10 w-full sm:w-80 rounded-lg" />;
  }

  return (
    // The width lives on the wrapper: `Select` fills whatever box it is given.
    <div className="w-full sm:w-80">
      <Select
        value={value?.tenantId ?? ""}
        onChange={(id) => onChange(tenants.find((t) => t.tenantId === id) ?? null)}
        options={options}
        placeholder="Select a tenant"
        searchable={options.length > 8}
        searchPlaceholder="Search tenants..."
        className="h-10"
      />
    </div>
  );
};

export default TenantPicker;
